import React, { useState, useEffect } from 'react';
import { distributionListsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  ChevronDown, 
  ChevronUp, 
  Mail, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Users,
  FileText
} from 'lucide-react';
import { formatDate } from '@/lib/dateUtils';

const STATUS_STYLES = {
  on_track: { bg: 'bg-green-100', text: 'text-green-800', label: 'On Track' },
  at_risk: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'At Risk' },
  off_track: { bg: 'bg-red-100', text: 'text-red-800', label: 'Off Track' }
};

const EmailHistorySection = ({ projectId, listId, listName }) => {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const loadHistory = async () => {
    if (loaded) return;
    setLoading(true);
    try {
      const response = await distributionListsAPI.getEmailHistory(projectId, listId);
      setHistory(response.data);
      setLoaded(true);
    } catch (error) {
      console.error('Failed to load email history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    if (!expanded && !loaded) {
      loadHistory();
    }
    setExpanded(!expanded);
  };

  return (
    <div className="mt-3 border-t pt-3" data-testid={`email-history-${listId}`}>
      <Button
        variant="ghost"
        size="sm"
        className="w-full justify-between text-slate-600 hover:text-slate-900 -mx-2 px-2"
        onClick={handleToggle}
        data-testid={`toggle-history-${listId}`}
      >
        <span className="flex items-center gap-2">
          <Mail className="h-4 w-4" />
          Email History
          {history.length > 0 && (
            <Badge variant="secondary" className="ml-1 font-normal">
              {history.length}
            </Badge>
          )}
        </span>
        {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </Button>

      {expanded && (
        <div className="mt-2 space-y-2">
          {loading ? (
            <div className="space-y-2">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-4 text-sm text-slate-500">
              <Mail className="h-8 w-8 mx-auto text-slate-300 mb-2" />
              No emails sent yet
            </div>
          ) : (
            history.map((delivery) => {
              const statusStyle = STATUS_STYLES[delivery.report_status] || STATUS_STYLES.on_track;
              
              return (
                <div 
                  key={delivery.id} 
                  className="border rounded-lg p-3 bg-slate-50"
                  data-testid={`delivery-${delivery.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {delivery.status === 'sent' ? (
                          <CheckCircle className="h-4 w-4 text-green-600 shrink-0" />
                        ) : (
                          <XCircle className="h-4 w-4 text-red-600 shrink-0" />
                        )}
                        <span className="font-medium text-sm truncate">
                          {delivery.subject}
                        </span>
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 mt-1">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatDate(delivery.sent_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {delivery.recipient_count} recipient{delivery.recipient_count !== 1 ? 's' : ''}
                        </span>
                        {delivery.report_status && (
                          <Badge className={`${statusStyle.bg} ${statusStyle.text} text-[10px] py-0`}>
                            {statusStyle.label}
                          </Badge>
                        )}
                      </div>

                      {delivery.status === 'failed' && delivery.error_message && (
                        <div className="mt-2 text-xs text-red-600 bg-red-50 rounded p-2">
                          Error: {delivery.error_message}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default EmailHistorySection;
